import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
} from "@coreui/react";
import React, { useEffect, useRef, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import {
  dismissConcurrencyAlert,
  getSnapshot,
  subscribe,
} from "src/services/concurrencyAlert";

const TITLE_ID = "concurrency-alert-title";
const BODY_ID = "concurrency-alert-body";

/*
  The control the user last focused outside this alert and outside Swal, which is where focus goes
  back on dismiss. Not document.activeElement at the moment the alert appears: a submit button is
  disabled while its request is in flight, and disabling a focused button drops focus to <body>
  before the failure even arrives.
*/
let lastFocused = null;
const rememberFocus = (event) => {
  const target = event.target;
  if (target?.closest?.('[role="dialog"], .swal2-container')) return;
  lastFocused = target;
};

/**
 * The server's concurrency message, in a modal only the user can close. Mounted once at the app
 * root, outside the routes, so neither navigation nor a stray Swal.close() can remove it. The
 * message is the server's own words and is rendered as text.
 */
export const ConcurrencyAlert = () => {
  const { t } = useTranslation();
  const alert = useSyncExternalStore(subscribe, getSnapshot);
  const confirmRef = useRef(null);
  const contentRef = useRef(null);
  const restoreRef = useRef(null);
  // Mounted once at the root, so this listens for the whole session.
  useEffect(() => {
    document.addEventListener("focusin", rememberFocus, true);
    return () => document.removeEventListener("focusin", rememberFocus, true);
  }, []);
  // Give focus back on dismiss to the control the user last used. The alert unmounts at once, so
  // CModal's own restore (onExited) never runs.
  useEffect(() => {
    if (alert) {
      if (!restoreRef.current) restoreRef.current = lastFocused;
      return;
    }
    const previous = restoreRef.current;
    restoreRef.current = null;
    if (previous?.isConnected && typeof previous.focus === "function") {
      previous.focus();
    }
  }, [alert]);
  // Focus the one control the moment it appears, so Enter or Space closes it.
  useEffect(() => {
    if (alert) confirmRef.current?.focus();
  }, [alert]);
  // CModal puts role="dialog" on its outer wrapper but hands our attributes to the inner
  // .modal-content, so the dialog's name and description are set where the role is, or a screen
  // reader announces an unnamed dialog.
  useEffect(() => {
    const dialog = contentRef.current?.closest('[role="dialog"]');
    if (!dialog) return;
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("aria-labelledby", TITLE_ID);
    dialog.setAttribute("aria-describedby", BODY_ID);
  }, [alert]);
  if (!alert) return null;
  const { message, tone } = alert;
  // OK is the only control, so Tab stays on it rather than walking the page behind the modal.
  const keepTabOnConfirm = (event) => {
    if (event.key !== "Tab") return;
    event.preventDefault();
    confirmRef.current?.focus();
  };

  return (
    <CModal
      show
      centered
      closeOnBackdrop={false}
      onClose={dismissConcurrencyAlert}
      onOpened={() => confirmRef.current?.focus()}
      color={tone === "info" ? "info" : "warning"}
      data-testid="dialog-concurrency"
      innerRef={contentRef}
      onKeyDown={keepTabOnConfirm}
    >
      <CModalHeader>
        <h5 id={TITLE_ID} className="modal-title">
          {tone === "info" ? t("Not saved") : t("Check before trying again")}
        </h5>
      </CModalHeader>
      <CModalBody>
        <div id={BODY_ID} data-testid="dialog-concurrency-body">
          {message}
        </div>
      </CModalBody>
      <CModalFooter>
        <CButton
          color="primary"
          innerRef={confirmRef}
          onClick={dismissConcurrencyAlert}
          data-testid="dialog-concurrency-confirm"
        >
          OK
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default ConcurrencyAlert;
